import { useState, useEffect } from "react"
import BaseModal from "../BaseComponents/BaseModal/BaseModal"
import ToggleSwitch from "../BaseComponents/Toggle/ToggleSwitch"
import SelectBox from "../BaseComponents/SelectBox/SelectBox"
import { useToast } from "../../context/ToastContext"
import { useCreatePage } from "../../api/cms/usePagesApi"
import styles from "./AddPageModal.module.css"
import {
  Settings,
  Globe,
  Search,
  ToggleRight,
  FileText,
  FileCode2
} from "lucide-react"

const META_ROBOTS_OPTIONS = [
  { value: "INDEX_FOLLOW", label: "Index, Follow" },
  { value: "NOINDEX_FOLLOW", label: "No Index, Follow" },
  { value: "NOINDEX_NOFOLLOW", label: "No Index, No Follow" },
]

const PAGE_CATEGORY_OPTIONS = [
  { value: "static", label: "Static Page" },
  { value: "content", label: "Content Page" },
]

const CONTENT_TYPE_OPTIONS = [
  { value: "destination", label: "Destination Page" },
  { value: "blog", label: "Blog Article" },
  { value: "inspiration", label: "Travel Inspiration" },
  { value: "campaign", label: "Campaign / Landing Page" },
]

const AddPageModal = ({ open, onClose, onSuccess }) => {
  const { showToast } = useToast()
  const [pageCategory, setPageCategory] = useState("static")
  const [contentType, setContentType] = useState("")
  const [title, setTitle] = useState("")
  const [slug, setSlug] = useState("")
  const [canonicalUrl, setCanonicalUrl] = useState("")
  const [metaTitle, setMetaTitle] = useState("")
  const [metaDescription, setMetaDescription] = useState("")
  const [content, setContent] = useState("")
  const [sortOrder, setSortOrder] = useState(0)
  const [metaRobots, setMetaRobots] = useState("INDEX_FOLLOW")
  const [publish, setPublish] = useState(false)

  const { execute: createPage, loading: creating, error: createError } = useCreatePage()

  useEffect(() => {
    if (!open) {
      setPageCategory("static")
      setContentType("")
      setTitle("")
      setSlug("")
      setCanonicalUrl("")
      setMetaTitle("")
      setMetaDescription("")
      setContent("")
      setSortOrder(0)
      setMetaRobots("INDEX_FOLLOW")
      setPublish(false)
    }
  }, [open])

  const handleSubmit = async (e) => {
    e?.preventDefault()
    try {
      await createPage({
        pageCategory: pageCategory.toUpperCase(),
        contentType: pageCategory === "content" && contentType ? contentType : null,
        sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
        status: publish ? "PUBLISHED" : "DRAFT",
        canonicalUrl: canonicalUrl.trim() || null,
        metaRobots,
        descriptions: [
          {
            languageCode: "en",
            title: title.trim() || "Untitled",
            slug: slug.trim() || "/",
            metaTitle: metaTitle.trim() || null,
            metaDescription: metaDescription.trim() || null,
            content: content.trim() || null,
          },
        ],
      })
      showToast("Page created successfully", "success")
      onSuccess?.()
      onClose?.()
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to create page", "error")
    }
  }

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      width="900px"
      title="Add New Page"
      footer={
        <div className={styles.footerActions}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSubmit}
            disabled={creating || !title.trim()}
          >
            {creating ? "Creating…" : "Create Page"}
          </button>
        </div>
      }
    >
      <p className={styles.subtitle}>
        Create a new static or content page with SEO and publishing options
      </p>

      {createError && (
        <div className={styles.error}>{createError}</div>
      )}

      <section className={styles.sectionCard}>
        <div className={styles.sectionTitle}>
          <FileText size={16} />
          <span>Page Setup</span>
        </div>

        <label className={styles.label}>Page Category</label>
        <SelectBox
          width="100%"
          placeholder="Select page category"
          value={pageCategory}
          options={PAGE_CATEGORY_OPTIONS}
          onChange={(e) => {
            setPageCategory(e.target.value)
            setContentType("")
          }}
        />

        {pageCategory === "content" && (
          <>
            <label className={styles.label}>Content Type</label>
            <SelectBox
              width="100%"
              placeholder="Select content type"
              value={contentType}
              options={CONTENT_TYPE_OPTIONS}
              onChange={(e) => setContentType(e.target.value)}
            />
          </>
        )}

        <label className={styles.label}>Sort Order</label>
        <input
          className={styles.input}
          type="number"
          min={0}
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        />
      </section>

      <section className={styles.sectionCard}>
        <div className={styles.sectionTitle}>
          <Settings size={16} />
          <span>Basic Information</span>
        </div>

        <label className={styles.label}>Page Title</label>
        <input
          className={styles.input}
          placeholder="Enter page title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <div className={styles.row}>
          <div className={styles.col}>
            <label className={styles.label}>Slug / URL</label>
            <div className={styles.inputWithIcon}>
              <span className={styles.prefix}>#</span>
              <input
                className={styles.input}
                placeholder="/page-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.col}>
            <label className={styles.label}>Canonical URL</label>
            <div className={styles.inputWithIcon}>
              <Globe size={14} className={styles.inputIcon} />
              <input
                className={styles.input}
                placeholder="https://example.com/page-slug"
                value={canonicalUrl}
                onChange={(e) => setCanonicalUrl(e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>

      <section className={styles.sectionCard}>
        <div className={styles.sectionTitle}>
          <Search size={16} />
          <span>SEO Settings</span>
        </div>

        <label className={styles.label}>Meta Title</label>
        <input
          className={styles.input}
          placeholder="Meta title"
          value={metaTitle}
          onChange={(e) => setMetaTitle(e.target.value)}
        />
        <span className={styles.helper}>{metaTitle.length} / 60 characters</span>

        <label className={styles.label}>Meta Description</label>
        <textarea
          className={styles.textarea}
          rows={3}
          placeholder="Meta description"
          value={metaDescription}
          onChange={(e) => setMetaDescription(e.target.value)}
        />
        <span className={styles.helper}>{metaDescription.length} / 160 characters</span>

        <label className={styles.label}>Meta Robots</label>
        <SelectBox
          width="100%"
          placeholder="Select meta robots"
          value={metaRobots}
          options={META_ROBOTS_OPTIONS}
          onChange={(e) => setMetaRobots(e.target.value)}
        />
      </section>

      <section className={styles.sectionCard}>
        <div className={styles.sectionTitle}>
          <FileCode2 size={16} />
          <span>Page Content</span>
        </div>

        <label className={styles.label}>Content (HTML / Text)</label>
        <textarea
          className={`${styles.textarea} ${styles.contentTextarea}`}
          rows={12}
          placeholder="Enter complete page content here"
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      </section>

      <section className={styles.sectionCard}>
        <div className={styles.sectionTitle}>
          <ToggleRight size={16} />
          <span>Publishing Status</span>
        </div>

        <div className={styles.statusRow}>
          <div>
            <p className={styles.statusTitle}>Publish Page</p>
            <p className={styles.statusDesc}>
              Enable to publish the page immediately
            </p>
          </div>

          <ToggleSwitch
            checked={publish}
            onChange={() => setPublish(!publish)}
          />
        </div>
      </section>
    </BaseModal>
  )
}

export default AddPageModal
